import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import { ValidationErrors, validate, formatError } from '@nakedplatform/support';
import {
  NativeSyntheticEvent,
  TextInput,
  TextInputChangeEventData,
  TextInputProps,
} from 'react-native';

import { Colors } from 'theme/colors';

import { InputField } from 'components/base/input';
import { Container } from 'theme/layout';

import { isEqual } from 'lodash';

/**
 * Creates a component of multiple text inputs.
 *
 * @param props - Render Props.
 */
export function MultiText(props: MultiTextProps) {
  const { defaults = {}, fields, onUpdate, onValidate, wrap, focusColor } = props;
  const [errors, setErrors] = useState<ValidationErrors>();
  const validations: Record<string, string> = getFieldValidations(fields);
  const [state, setState] = useState<Record<string, string>>(
    generateStateFromFields(fields, defaults, wrap)
  );
  const textInputRefs = useRef<((TextInput & HTMLInputElement) | null)[]>([]);
  const focusNextField = useCallback(
    (index: number) => () => {
      if (textInputRefs.current && textInputRefs.current[index + 1]) {
        textInputRefs.current[index + 1].focus();
      }
    },
    []
  );
  const fieldsLen = fields.length;
  const enabledFieldStates = limitToEnabledFields(state, fields);
  const enabledFieldValidations = limitToEnabledFields(validations, fields);
  const validation = validate(enabledFieldStates, enabledFieldValidations);

  useEffect(() => {
    textInputRefs.current = new Array(fieldsLen);
  }, [fieldsLen]);

  useEffect(() => {
    onValidate(validation.isValid);

    if (!isEqual(validation.errors, errors)) {
      setErrors(validation.errors);
    }
  }, [validation]);

  useEffect(() => {
    if (onUpdate) {
      onUpdate(enabledFieldStates);
    }
  }, [onUpdate, state]);

  const onChangeText = useCallback(
    (key: string) => (value: string) => {
      const newState = { ...state, [key]: value };
      setState(newState);
    },
    [state]
  );

  return (
    <Container
      width="100%"
      flexDirection={['column', 'row']}
      justifyContent="center"
      alignItems={['center', 'flex-end']}
    >
      {fields.map((field, index) => {
        const isFirst = index === 0;
        const isLast = fieldsLen === index + 1;
        const { name, placeholder, keyboardType, disabled } = field;
        const error = errors && errors[name];

        return (
          <TextInputComponent
            onChangeText={onChangeText(name)}
            key={name}
            placeholder={placeholder}
            value={state[name]}
            ref={el => {
              textInputRefs.current[index] = el as TextInput & HTMLInputElement;
            }}
            onSubmitEditing={isLast ? null : focusNextField(index)}
            isFirst={isFirst}
            isLast={isLast}
            error={error}
            name={name}
            editable={!disabled}
            keyboardType={keyboardType}
            focusColor={focusColor}
          />
        );
      })}
    </Container>
  );
}
/**
 * Filters an object, including only the keys for fields that are enabled
 *
 * @param fieldsObject - The object to filter. Keys in this object must represent fields
 * @param fields - All fields rendered in the multitext component
 */
function limitToEnabledFields(fieldsObject: Record<string, string>, fields: MultiTextInput[]) {
  const objectAsEntries = Object.entries(fieldsObject);
  const enabledObjectEntries = objectAsEntries.filter(([fieldName]) => {
    return fields.some(({ name, disabled }) => name === fieldName && !disabled);
  });

  return enabledObjectEntries.reduce((state, [fieldName, value]) => {
    state[fieldName] = value;

    return state;
  }, {});
}
/**
 * Generates a 'state' object that will be used to map input field names to the corresponding inputs' values.
 * If the user previously completed a field, their previous answer will be populated.
 *
 * @param fields - Input fields that will be rendered by the multitext component
 * @param previousAnswers - Previous answers that the user provided for each field
 * @param keyPrefix - The prefix that will be prepended to each previous answer the user gave
 */
function generateStateFromFields(
  fields: MultiTextInput[],
  previousAnswers: Record<string, string>,
  keyPrefix?: string
) {
  const fieldsArePrefixedWithKey = keyPrefix
    ? Object.keys(previousAnswers).every(fieldName => fieldName.startsWith(`${keyPrefix}:`))
    : false;

  return fields.reduce((state, { name: fieldName }) => {
    const previousAnswerKey = fieldsArePrefixedWithKey ? `${keyPrefix}:${fieldName}` : fieldName;

    state[fieldName] = previousAnswers?.[previousAnswerKey] ?? '';

    return state;
  }, {});
}
/**
 * Returns the validations object, which maps field names to a description of the validation to apply to that field
 *
 * @param fields - List of fields to be rendered by the multitext component
 */
function getFieldValidations(fields: MultiTextInput[]) {
  return fields.reduce((validations, { name, validation }) => {
    if (validation) {
      validations[name] = validation;
    }

    return validations;
  }, {});
}
/**
 * Renders a TextInput component with border.
 *
 * @param props - Text input render props.
 */
// eslint-disable-next-line react/display-name
const TextInputComponent = forwardRef<TextInput, TextInputComponentProps>(
  (props, ref: React.MutableRefObject<TextInput & HTMLInputElement>) => {
    // eslint-disable-next-line react/prop-types
    const {
      placeholder,
      value,
      onChangeText,
      onSubmitEditing,
      isLast,
      isFirst,
      editable,
      focusColor,
      ...otherProps
    } = props;

    const [focusBorderColor, setFocusBorderColor] = useState<string>(Colors.alto);
    const [touched, setTouched] = useState(false);
    const [shouldValidateInput, setShouldValidateInput] = useState(false);
    const { error, name, keyboardType } = otherProps;
    const backgroundColor = editable ? Colors.white : Colors.gallery;
    const textColor = editable ? Colors.thunder : Colors.darkGrey;
    const borderColor = editable ? focusBorderColor : Colors.gallery;

    const handleOnFocus = useCallback(() => {
      setFocusBorderColor(focusColor ?? Colors.green);
      setTouched(true);
    }, [focusColor]);
    const handleOnBlur = useCallback(() => {
      setShouldValidateInput(true);
      setFocusBorderColor(Colors.alto);
    }, []);
    const handleOnChange = useCallback(
      (e: NativeSyntheticEvent<TextInputChangeEventData>) => onChangeText(e.nativeEvent.text),
      [onChangeText]
    );
    const defaultMarginRight = isLast ? '0px' : '10px';

    // Calculates the spacing requirements: each field adds space above itself when it has an error
    const wouldShowError = touched && shouldValidateInput && error && error.length > 0;
    const errorMessage = wouldShowError ? formatError(error, placeholder, name || '') : undefined;
    const hasError = Boolean(errorMessage);

    // Base spacing is 10px, add 33px for error height when current field has error (but not for first field)
    const dynamicMarginTop = !isFirst && hasError ? '43px' : '10px';

    return (
      <Container
        marginRight={['0px', defaultMarginRight]}
        marginTop={[dynamicMarginTop, '0px']}
        width="100%"
        style={{
          transition: 'margin-top 0.3s ease-in-out',
        }}
      >
        <InputField
          ref={ref}
          color={textColor}
          backgroundColor={backgroundColor}
          onChange={handleOnChange}
          placeholder={placeholder}
          placeholderTextColor={Colors.darkGrey}
          value={value}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onSubmitEditing={onSubmitEditing}
          borderColor={borderColor}
          error={error}
          editable={editable}
          name={name}
          touched={touched}
          shouldValidateInput={shouldValidateInput}
          keyboardType={keyboardType}
        />
      </Container>
    );
  }
);

/* Type definitions */
export interface MultiTextProps {
  /** List of input fields. */
  fields: MultiTextInput[];
  /** The default values */
  defaults?: Record<string, string>;
  /** Emits on `onUpdate` event */
  onUpdate?(payload: Record<string, string>): void;
  /** Emits on `onValidate` event */
  onValidate?(isValid: boolean): void;
  /** Prefix for the field name */
  wrap?: string;
  /** Which color to wrap inputs in when they are focused */
  focusColor?: string;
}

export interface MultiTextInput {
  /** Whether or not the field should be disabled */
  disabled?: boolean;
  /** Name of the input field. */
  name: string;
  /** Placeholder for the input field. */
  placeholder: string;
  /** Validation rules for the input field. */
  validation: string;
  /** Sets keyboard type */
  keyboardType?: TextInputProps['keyboardType'];
}

interface TextInputComponentProps extends TextInputProps {
  /** Flag to tell if it's the first TextInputComponent */
  isFirst: boolean;
  /** Flag to tell if it's the last TextInputComponent */
  isLast: boolean;
  /** The error to show on a field */
  error?: string[];
  /** The field name */
  name?: string;
  /** Sets keyboard type */
  keyboardType?: TextInputProps['keyboardType'];
  /** Which color to wrap inputs in when they are focused */
  focusColor?: string;
}
